import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MapPin, Calendar, Users, Sparkles, ArrowRight, LogOut, ShieldCheck, LayoutGrid, List } from "lucide-react";
import SessionsMap from "../components/SessionsMap";
import SessionsCalendar from "../components/SessionsCalendar";

const DISCIPLINES = [
  { value: "all", label: "Toutes" },
  { value: "nordic_yoga", label: "Nordic Yoga" },
  { value: "marche_nordique", label: "Marche Nordique" },
  { value: "stage", label: "Stages" },
];

const HERO_IMG = "https://customer-assets-agu9un31.emergentagent.net/job_nordic-yoga-planner/artifacts/4s362i4m_WhatsApp%20Image%202024-05-21%20%C3%A0%2016.17.57_03e7752d.jpg";
const MARCHE_IMG = "https://customer-assets-agu9un31.emergentagent.net/job_nordic-yoga-planner/artifacts/yyiqz9du_5598375-sports-3340697.webp";
const MEDIT_IMG = "https://customer-assets-agu9un31.emergentagent.net/job_nordic-yoga-planner/artifacts/em2lg833_IMG_4608.webp";
const SENTIER_IMG = "https://customer-assets-agu9un31.emergentagent.net/job_nordic-yoga-planner/artifacts/dh8bhls7_WhatsApp%20Image%202024-04-23%20%C3%A0%2019.38.01_418bd2d3.jpg";

function fmt(d) {
  return new Date(d).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
}

export default function Landing() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [carnets, setCarnets] = useState([]);
  const [mapFilter, setMapFilter] = useState("all");
  const [sessionFilter, setSessionFilter] = useState("all");
  const [sessionsView, setSessionsView] = useState("cards"); // "cards" | "calendar"

  useEffect(() => {
    api.get("/sessions").then(r => setSessions(r.data)).catch(() => {});
    api.get("/carnets").then(r => setCarnets(r.data)).catch(() => {});
  }, []);

  // Cards view stays limited to 12; calendar shows the full agenda.
  const cardSessions = sessions.slice(0, 12);
  const mapSessions = mapFilter === "all" ? sessions : sessions.filter(s => s.discipline === mapFilter);

  return (
    <div className="min-h-screen" style={{ background: "var(--sand)" }}>
      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-50" style={{ background: "rgba(249,246,240,0.85)", backdropFilter: "blur(12px)", borderBottom: "1px solid rgba(46,90,68,0.08)" }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="font-serif-display text-2xl font-semibold" style={{ color: "var(--pine)" }} data-testid="landing-logo">
            Nordic <span style={{ color: "var(--terracotta)" }}>Yoga Belgium</span> · Agenda
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="#seances" className="hover:text-[var(--forest)]" data-testid="nav-sessions">Séances</a>
            <a href="#carnets" className="hover:text-[var(--forest)]" data-testid="nav-carnets">Formules</a>
            <a href="#lieux" className="hover:text-[var(--forest)]" data-testid="nav-locations">Nos lieux</a>
            <Link to="/boutique" className="hover:text-[var(--forest)]" data-testid="nav-shop">Boutique</Link>
          </nav>
          <div className="flex items-center gap-3">
            {user ? (
              <>
                <button onClick={() => nav(user.role === "admin" || user.role === "coach" ? "/admin" : "/mon-espace")} className="btn-secondary" data-testid="nav-dashboard">
                  Mon espace
                </button>
                <button onClick={logout} className="btn-ghost" data-testid="nav-logout"><LogOut size={16}/></button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn-ghost text-sm" data-testid="nav-login">Connexion</Link>
                <Link to="/login?tab=register" className="btn-primary text-sm" data-testid="nav-register">S'inscrire</Link>
                <Link to="/login?admin=1" className="hidden lg:inline-flex items-center gap-1 text-xs text-[var(--muted-text)] hover:text-[var(--pine)] px-2 py-1 rounded-full border border-transparent hover:border-[var(--pine)]/30" data-testid="nav-admin-login">
                  <ShieldCheck size={12}/> Espace admin
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-24">
        <div className="relative h-[85vh] max-h-[720px] overflow-hidden">
          <img src={HERO_IMG} alt="Champ de coquelicots et bleuets — Belgique" className="absolute inset-0 w-full h-full object-cover object-center" />
          <div className="absolute inset-0 hero-overlay" />
          <div className="relative max-w-7xl mx-auto h-full flex flex-col justify-end px-6 pb-20 text-white">
            <span className="overline" style={{ color: "#F9E4C2" }}>Nordic Yoga® · Marche Nordique · Bien-être</span>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-semibold mt-4 max-w-3xl leading-none">
              Respirer, marcher, s'étirer <em style={{ color: "#F5C88A" }}>en pleine nature.</em>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-white/85">
              Réservez vos séances, suivez votre carnet, retrouvez-nous sur les sentiers. Une plateforme pensée pour nos participants.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/login?tab=register" className="btn-primary" data-testid="hero-cta-register">
                Réserver ma première séance <ArrowRight size={18} />
              </Link>
              <a href="#seances" className="btn-ghost text-white border-white/40 hover:border-white" data-testid="hero-cta-agenda">
                Voir l'agenda
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Disciplines */}
      <section className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-10">
        <div className="card-warm p-8">
          <img src={MEDIT_IMG} alt="Étirements Nordic Yoga en forêt d'automne" className="w-full h-56 object-cover rounded-lg mb-6" style={{ transform: "rotate(180deg)" }} />
          <span className="overline">Discipline</span>
          <h3 className="font-serif-display text-3xl font-semibold mt-2" style={{ color: "var(--pine)" }}>Nordic Yoga®</h3>
          <p className="mt-4 text-[var(--muted-text)]">Fusion douce entre postures de yoga, respiration profonde et éveil du corps dans les paysages du Nord. Idéal en début de journée.</p>
        </div>
        <div className="card-warm p-8">
          <img src={MARCHE_IMG} alt="Séance de Marche Nordique en forêt" className="w-full h-56 object-cover rounded-lg mb-6" />
          <span className="overline">Discipline</span>
          <h3 className="font-serif-display text-3xl font-semibold mt-2" style={{ color: "var(--pine)" }}>Marche Nordique</h3>
          <p className="mt-4 text-[var(--muted-text)]">Un sport complet, alliant cardio et travail des membres supérieurs grâce aux bâtons spécifiques. Rejoignez nos sorties encadrées.</p>
        </div>
      </section>

      {/* Sessions */}
      <section id="seances" className="max-w-7xl mx-auto px-6 py-20">
        {(() => {
          const discovery = sessions.filter(s => s.is_discovery && !s.cancelled).slice(0, 3);
          if (discovery.length === 0) return null;
          return (
            <div className="mb-16 rounded-3xl overflow-hidden" style={{ background: "linear-gradient(135deg, var(--terracotta) 0%, #B85F35 100%)" }} data-testid="landing-discovery-section">
              <div className="p-8 md:p-12 text-white">
                <div className="flex items-center gap-2 text-[13px] uppercase tracking-widest opacity-85">
                  <span>🌱</span> Séances Découverte
                </div>
                <h2 className="font-serif-display text-3xl md:text-4xl font-semibold mt-3 max-w-2xl">
                  Envie de nous rencontrer ? Rejoignez une <em>séance offerte</em>.
                </h2>
                <p className="mt-3 max-w-2xl text-white/85">
                  Une invitation ouverte pour découvrir notre approche avant de vous engager. Places limitées, réservation en 30 secondes.
                </p>
                <div className="mt-8 grid md:grid-cols-3 gap-4">
                  {discovery.map(s => (
                    <div key={s.id} className="bg-white/10 backdrop-blur rounded-xl p-5 border border-white/20" data-testid={`landing-discovery-${s.id}`}>
                      <div className="text-[11px] uppercase tracking-wide opacity-80">
                        {s.discipline === "nordic_yoga" ? "Nordic Yoga" : s.discipline === "marche_nordique" ? "Marche Nordique" : "Stage"}
                      </div>
                      <div className="font-serif-display text-xl font-semibold mt-1">{s.title}</div>
                      <div className="mt-3 text-sm opacity-90 flex items-center gap-2"><Calendar size={14}/>{fmt(s.start_at)}</div>
                      <div className="text-sm opacity-90 flex items-center gap-2"><MapPin size={14}/>{s.location}</div>
                      <div className="mt-4 pt-4 border-t border-white/20 flex items-center justify-between">
                        <span className="text-xs opacity-85">{s.places_left} places</span>
                        <Link to="/login?tab=register" className="text-[13px] px-3 py-1.5 rounded-full bg-white text-[var(--terracotta)] font-semibold hover:bg-white/90" data-testid={`landing-discovery-cta-${s.id}`}>Je réserve →</Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <span className="overline">Agenda</span>
            <h2 className="font-serif-display text-4xl lg:text-5xl font-semibold mt-2" style={{ color: "var(--pine)" }}>Prochaines sorties</h2>
          </div>
          <Link to="/login?tab=register" className="text-sm hidden md:inline-flex items-center gap-1 hover:text-[var(--forest)]">
            Toutes les séances <ArrowRight size={14} />
          </Link>
        </div>
        <div className="flex items-center gap-2 flex-wrap mb-8" data-testid="landing-sessions-filters">
          {DISCIPLINES.map(f => {
            const count = f.value === "all" ? cardSessions.length : cardSessions.filter(s => s.discipline === f.value).length;
            return (
              <button key={f.value} onClick={() => setSessionFilter(f.value)}
                className={`px-4 py-1.5 rounded-full text-sm inline-flex items-center gap-1.5 transition ${sessionFilter === f.value ? "bg-[var(--pine)] text-white" : "bg-white text-[var(--muted-text)] border border-slate-200 hover:border-[var(--forest)]"}`}
                data-testid={`landing-sessions-filter-${f.value}`}>
                {f.label}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${sessionFilter === f.value ? "bg-white/20" : "bg-slate-100"}`}>{count}</span>
              </button>
            );
          })}
          <div className="ml-auto inline-flex items-center rounded-full bg-slate-100 p-1 text-xs" data-testid="landing-sessions-view-toggle">
            {[
              { v: "cards", label: "Cartes", icon: List },
              { v: "calendar", label: "Calendrier", icon: LayoutGrid },
            ].map(o => {
              const Ico = o.icon;
              return (
                <button key={o.v} onClick={() => setSessionsView(o.v)}
                  className={`px-3 py-1 rounded-full transition inline-flex items-center gap-1 ${sessionsView === o.v ? "bg-[var(--pine)] text-white" : "text-[var(--muted-text)] hover:text-[var(--pine)]"}`}
                  data-testid={`landing-sessions-view-${o.v}`}>
                  <Ico size={12}/> {o.label}
                </button>
              );
            })}
          </div>
        </div>
        {sessions.length === 0 ? (
          <div className="text-[var(--muted-text)] text-center py-12 card-warm">Aucune séance programmée pour l'instant.</div>
        ) : sessionsView === "calendar" ? (
          <SessionsCalendar
            sessions={sessionFilter === "all" ? sessions : sessions.filter(s => s.discipline === sessionFilter)}
            onSelect={() => nav("/login?tab=register")}
            testidPrefix="landing-sessions-calendar"
          />
        ) : (() => {
          const groups = [
            { key: "nordic_yoga", label: "Nordic Yoga®", cls: "badge-yoga", chipLabel: "Nordic Yoga" },
            { key: "marche_nordique", label: "Marche Nordique", cls: "badge-marche", chipLabel: "Marche Nordique" },
            { key: "stage", label: "Stages", cls: "badge-stage", chipLabel: "Stage" },
          ];
          const visible = sessionFilter === "all" ? groups : groups.filter(g => g.key === sessionFilter);
          const anyVisible = visible.some(g => cardSessions.some(i => i.discipline === g.key));
          if (!anyVisible) {
            return <div className="text-[var(--muted-text)] text-center py-12 card-warm">Aucune séance dans cette catégorie.</div>;
          }
          return (
            <div className="space-y-12">
              {visible.map(g => {
                const list = cardSessions.filter(s => s.discipline === g.key);
                if (list.length === 0) return null;
                return (
                  <div key={g.key} data-testid={`landing-sessions-group-${g.key}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <span className={`badge-discipline ${g.cls}`}>{g.label}</span>
                      <span className="text-xs text-[var(--muted-text)]">{list.length} séance{list.length > 1 ? "s" : ""}</span>
                      <div className="flex-1 h-px bg-slate-200"></div>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {list.map(s => (
                        <div key={s.id} className="card-warm p-6 flex flex-col" data-testid={`session-card-${s.id}`}>
                          <div className="flex items-center justify-between">
                            <span className={`badge-discipline ${g.cls}`}>{g.chipLabel}</span>
                            <span className="text-xs text-[var(--muted-text)]">{s.places_left} places</span>
                          </div>
                          <div className="mt-3 flex items-center gap-2 flex-wrap">
                            {s.is_discovery && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[var(--forest)] text-white uppercase tracking-wide" data-testid={`session-discovery-${s.id}`}>🌱 Découverte</span>
                            )}
                          </div>
                          <h3 className="font-serif-display text-2xl font-semibold mt-2" style={{ color: "var(--pine)" }}>{s.title}</h3>
                          <div className="mt-3 flex items-center gap-2 text-sm text-[var(--muted-text)]"><Calendar size={14}/>{fmt(s.start_at)}</div>
                          <div className="flex items-center gap-2 text-sm text-[var(--muted-text)]"><MapPin size={14}/>{s.location}</div>
                          <div className="mt-5 pt-5 border-t border-slate-100 flex items-center justify-between">
                            {Number(s.price_eur) === 0 ? (
                              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-[var(--terracotta)] text-white uppercase tracking-wide" data-testid={`session-free-${s.id}`}>Offert</span>
                            ) : (
                              <span className="font-semibold text-lg" style={{ color: "var(--terracotta)" }}>{s.price_eur} €</span>
                            )}
                            <Link to="/login" className="btn-secondary text-sm">Réserver</Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </section>

      {/* Carnets */}
      <section id="carnets" className="py-24" style={{ background: "var(--forest-light)" }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="overline">Formules</span>
            <h2 className="font-serif-display text-4xl lg:text-5xl font-semibold mt-2" style={{ color: "var(--pine)" }}>Carnets d'abonnement</h2>
            <p className="mt-4 text-[var(--muted-text)] max-w-xl mx-auto">Un tarif dégressif au fil des séances. Utilisez vos crédits à votre rythme.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {carnets.map((c, i) => (
              <div key={c.id} className={`p-8 rounded-2xl ${i === 1 ? "bg-[var(--pine)] text-white" : "bg-white"}`} data-testid={`carnet-${c.id}`}>
                <div className="flex items-center gap-2">
                  <Sparkles size={18} className={i === 1 ? "text-[#F5C88A]" : "text-[var(--terracotta)]"} />
                  <span className={`overline ${i === 1 ? "!text-[#F5C88A]" : ""}`}>{i === 1 ? "Populaire" : "Découverte"}</span>
                </div>
                <h3 className="font-serif-display text-3xl font-semibold mt-4">{c.name}</h3>
                <div className="mt-4 flex items-end gap-2">
                  <span className="text-5xl font-serif-display font-semibold">{c.price_eur}</span>
                  <span className="pb-2">€</span>
                </div>
                <p className={`mt-2 text-sm ${i === 1 ? "text-white/70" : "text-[var(--muted-text)]"}`}>Soit {(c.price_eur / c.sessions_count).toFixed(2)} € par séance</p>
                <Link to="/login" className={`mt-6 inline-block ${i === 1 ? "btn-primary" : "btn-secondary"}`} data-testid={`carnet-buy-${c.id}`}>Choisir ce carnet</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Location teaser */}
      <section id="lieux" className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
          <div>
            <span className="overline">Nos points de rendez-vous</span>
            <h2 className="font-serif-display text-4xl font-semibold mt-2" style={{ color: "var(--pine)" }}>Marcher là où respire la forêt.</h2>
            <p className="mt-4 text-[var(--muted-text)]">Chaque séance est accompagnée d'un lieu de rendez-vous précis. Retrouvez ci-dessous la carte de nos prochaines sorties — cliquez sur un point pour découvrir la séance et vous inscrire.</p>
            <div className="mt-8 flex items-center gap-3 text-sm text-[var(--muted-text)]">
              <Users size={16} className="text-[var(--forest)]" /> Petits groupes de 8 à 12 personnes
            </div>
          </div>
          <div className="card-warm p-2 overflow-hidden">
            <img src={SENTIER_IMG} alt="Sentier sur passerelle en bois" className="w-full h-80 object-cover rounded-xl" />
          </div>
        </div>
        <div data-testid="landing-map">
          <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="landing-map-filters">
            {DISCIPLINES.map(d => (
              <button
                key={d.value}
                onClick={() => setMapFilter(d.value)}
                className={`px-4 py-1.5 rounded-full text-sm transition ${mapFilter === d.value ? "bg-[var(--pine)] text-white" : "bg-white text-[var(--muted-text)] border border-slate-200 hover:border-[var(--forest)]"}`}
                data-testid={`landing-map-filter-${d.value}`}
              >{d.label}</button>
            ))}
            <span className="text-xs text-[var(--muted-text)] ml-auto">{mapSessions.filter(s => s.lat != null).length} lieu(x) sur la carte</span>
          </div>
          <SessionsMap sessions={mapSessions} height="440px" />
        </div>
      </section>

      <footer className="py-10 text-center text-sm text-[var(--muted-text)] border-t border-slate-200">
        <div>© {new Date().getFullYear()} Nordic Yoga Belgium · Marche Nordique — Construit avec soin.</div>
        <div className="mt-2">
          <Link to="/login?admin=1" className="inline-flex items-center gap-1 text-xs hover:text-[var(--pine)]" data-testid="footer-admin-login">
            <ShieldCheck size={12}/> Connexion administrateur / coach
          </Link>
        </div>
      </footer>
    </div>
  );
}
